/**
 * The first-release waitlist.
 *
 * Progressive enhancement over Shopify's own customer form: without this file
 * the form posts, Shopify creates the contact and redirects back, and the
 * confirmation renders from `form.posted_successfully?`. With it, the same
 * post happens over fetch so the page does not reload and the confirmation
 * replaces the field in place.
 *
 * Nothing here decides whether a signup succeeded. Success is read from where
 * Shopify redirects the post to — `?customer_posted=true` — so a rejected
 * address cannot be confirmed by a click.
 *
 * The email address is never put into an analytics event, a tag, or the URL.
 */

const CUSTOMER_POSTED = 'customer_posted';

/** Tag-safe: lowercase, single underscores, short enough to stay readable in admin. */
function slug(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 32);
}

function track(name, payload) {
  try {
    window.Shopify?.analytics?.publish?.(name, payload);
  } catch (error) {
    /* Analytics is never allowed to break a signup. */
  }
}

/** Campaign context from the URL, as tags. Absent params contribute nothing. */
function campaignTags() {
  const tags = [];

  try {
    const params = new URLSearchParams(window.location.search);
    const utmSource = slug(params.get('utm_source'));
    const utmCampaign = slug(params.get('utm_campaign'));

    if (utmSource) tags.push(`utm_${utmSource}`);
    if (utmCampaign) tags.push(`campaign_${utmCampaign}`);
  } catch (error) {
    /* No URL API, or an unparseable query string: no campaign tags. */
  }

  return tags;
}

/**
 * Reads the outcome of a customer-form post.
 *
 * Shopify redirects a successful post back to the page with
 * `?customer_posted=true`. Anything else is a re-render of the page carrying
 * the rejection, which the theme prints into [data-az-wl-error].
 */
async function readOutcome(response) {
  if (response.url && response.url.includes(`${CUSTOMER_POSTED}=true`)) {
    return { ok: true };
  }

  let message = '';

  try {
    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const printed = parsed.querySelector('[data-az-wl-error]')?.textContent?.trim();
    const fallback = parsed.querySelector('.errors li, .form__message--error')?.textContent?.trim();
    message = printed || fallback || '';
  } catch (error) {
    /* Unreadable response: handled as a retry below. */
  }

  // Shopify's wording for an address it already holds. Said plainly, because
  // it is not a failure — they are on the list either way.
  if (/already been taken|already exists/i.test(message)) {
    return { ok: true, already: true };
  }

  if (message) return { ok: false, message };

  // A rejected post that did not come back carrying a reason is not a rejected
  // address — it is Shopify refusing this particular request. Its bot check
  // answers a background fetch with a challenge page, and a challenge is
  // something only a real navigation can complete. So the post is handed back
  // to the browser rather than shown to the visitor as an error they cannot
  // act on.
  return { ok: false, retryNatively: true };
}

class AzWaitlist extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.formPane = this.querySelector('[data-az-wl-form]');
    this.donePane = this.querySelector('[data-az-wl-done]');
    this.input = this.querySelector('.az-wl__input');
    this.submit = this.querySelector('[data-az-wl-submit]');
    this.errorNode = this.querySelector('[data-az-wl-error]');
    this.statusNode = this.querySelector('[data-az-wl-status]');
    this.tagsInput = this.querySelector('[data-az-wl-tags]');
    this.interest = this.querySelector('[data-az-wl-interest]');

    if (!this.form || !this.input || !this.submit) return;

    this.submitLabel = this.submit.textContent.trim();
    this.addCampaignTags();

    this.form.addEventListener('submit', this.handleSubmit.bind(this));

    // Browser validation reads as a bubble on the field; the theme's own
    // message belongs in the same place every other message appears.
    this.input.addEventListener('invalid', this.handleInvalid.bind(this));
    this.input.addEventListener('input', this.clearError.bind(this));

    if (this.interest) {
      this.interest.addEventListener('click', this.handleInterest.bind(this));
    }

    // A signup that already completed server-side, on a page that reloaded.
    if (this.donePane && !this.donePane.hidden) {
      this.revealInterest();
      this.cleanUrl();
    }

    this.observeView();
  }

  get context() {
    return {
      source: this.dataset.source || 'site',
      product_handle: this.dataset.productHandle || null,
      product_title: this.dataset.productTitle || null,
      page_type: document.querySelector('#MainContent')?.dataset.template || null,
    };
  }

  addCampaignTags() {
    if (!this.tagsInput) return;

    const tags = campaignTags();
    if (tags.length) this.tagsInput.value = `${this.tagsInput.value}, ${tags.join(', ')}`;
  }

  /** One `waitlist_view` per instance, when it is actually looked at. */
  observeView() {
    if (typeof IntersectionObserver !== 'function') return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          observer.disconnect();
          track('waitlist_view', this.context);
        }
      },
      { threshold: 0.4 }
    );

    observer.observe(this);
  }

  handleInvalid(event) {
    event.preventDefault();
    this.showError(
      this.input.value.trim() === ''
        ? 'Please enter your email address.'
        : 'Please enter a valid email address.'
    );
    this.input.focus();
  }

  showError(message) {
    if (this.errorNode) this.errorNode.textContent = message;
    this.input.setAttribute('aria-invalid', 'true');
  }

  clearError() {
    if (this.errorNode) this.errorNode.textContent = '';
    this.input.removeAttribute('aria-invalid');
  }

  /** A live-region message that changes nothing about the form itself. */
  announce(message) {
    if (this.statusNode) this.statusNode.textContent = message;
  }

  setBusy(busy) {
    this.submit.disabled = busy;
    this.form.setAttribute('aria-busy', String(busy));
    this.submit.textContent = busy ? 'Joining…' : this.submitLabel;
    if (this.statusNode) this.statusNode.textContent = busy ? 'Joining the first release.' : '';
  }

  /**
   * Whether Shopify is protecting forms on this storefront with its captcha.
   *
   * It mints a token only in response to a real submit event, and injects it
   * into the form just before posting. Intercepting the submit therefore
   * guarantees a post with no token, which Shopify answers with a full-page
   * "Missing CAPTCHA token" error — observed on this store. So where the
   * protection is present, the browser is left to submit the form itself and
   * the confirmation is the server-rendered one.
   */
  get captchaProtected() {
    return typeof window.Shopify?.captcha?.protect === 'function';
  }

  async handleSubmit(event) {
    if (!this.form.checkValidity()) return;

    // A second pass, asked for by the recovery path below: let it through
    // untouched so Shopify's own submit handling runs.
    if (this.nativePass) return;

    if (this.captchaProtected) {
      track('waitlist_submit', { ...this.context, transport: 'native' });
      // Deliberately NOT setBusy(): that disables the submit button, and a
      // submitter disabled from inside its own submit event cancels the
      // submission — and would also break the captcha's own
      // preventDefault-get-token-resubmit cycle. Nothing here may touch the
      // form; the browser and Shopify own this submission completely.
      this.announce('Joining the first release.');
      return;
    }

    event.preventDefault();
    this.clearError();
    this.setBusy(true);
    track('waitlist_submit', this.context);

    // Kept for the optional interest step, which posts the same contact again.
    this.email = this.input.value.trim();

    let outcome;

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        headers: { Accept: 'text/html' },
      });
      outcome = await readOutcome(response);
    } catch (error) {
      // The network, not the address. Hand the post back to the browser rather
      // than leaving someone stuck in front of a button that does nothing.
      this.submitNatively();
      return;
    }

    this.setBusy(false);

    if (outcome.retryNatively) {
      this.announce('Joining the first release.');
      this.submitNatively();
      return;
    }

    if (!outcome.ok) {
      this.showError(outcome.message || 'Something went wrong. Please try again.');
      track('waitlist_error', this.context);
      return;
    }

    this.succeed(outcome.already);
  }

  /**
   * Re-submits through a real submit event, so every handler Shopify has put on
   * the form — its captcha above all — gets to run. `form.submit()` would fire
   * no event and skip them, which is what produced the missing-token error.
   */
  submitNatively() {
    this.nativePass = true;
    // Re-enable first: a disabled submitter cancels the submission.
    this.setBusy(false);
    this.form.requestSubmit();
  }

  succeed(already) {
    track('waitlist_success', { ...this.context, already_subscribed: Boolean(already) });

    if (already && this.donePane) {
      const heading = this.donePane.querySelector('.az-wl__done-heading');
      if (heading) heading.textContent = "You're already on the list.";
    }

    if (this.formPane) this.formPane.hidden = true;

    if (this.donePane) {
      this.donePane.hidden = false;
      this.revealInterest();
      // Moves the reading position to the confirmation and announces it,
      // without scrolling the page out from under the reader.
      this.donePane.focus({ preventScroll: true });
    }
  }

  revealInterest() {
    if (this.interest) this.interest.hidden = false;
  }

  /**
   * Drops `?customer_posted=true` after a no-JavaScript signup so a refresh
   * does not re-show the confirmation. Cosmetic only — the contact was created
   * server-side and nothing here re-reads this value to decide anything.
   */
  cleanUrl() {
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has(CUSTOMER_POSTED)) return;
      url.searchParams.delete(CUSTOMER_POSTED);
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    } catch (error) {
      /* Nothing depends on this; the confirmation is already on screen. */
    }
  }

  /**
   * The optional second step, off by default.
   *
   * The contact already exists, so this posts the same address again with one
   * more tag. Shopify does not guarantee that a customer form updates a record
   * it already holds, which is why the question ships switched off and why
   * nothing else is routed through here: the signup, its source and any
   * product-context interest are all written by the first post.
   *
   * Best effort by design. A failure here is silent to the visitor because
   * they have already joined, and telling them otherwise would be false.
   */
  async handleInterest(event) {
    const chip = event.target.closest('[data-az-wl-choice]');
    if (!chip || !this.email) return;

    const choice = chip.dataset.azWlChoice;

    for (const button of this.interest.querySelectorAll('[data-az-wl-choice]')) {
      button.disabled = true;
      button.classList.toggle('az-wl__chip--chosen', button === chip);
    }

    const body = new FormData();
    for (const [key, value] of new FormData(this.form).entries()) {
      if (key === 'contact[email]' || key === 'contact[tags]') continue;
      body.append(key, value);
    }
    body.append('contact[email]', this.email);
    body.append('contact[tags]', `${this.tagsInput ? this.tagsInput.value : ''}, ${choice}`);

    try {
      await fetch(this.form.action, { method: 'POST', body, headers: { Accept: 'text/html' } });
    } catch (error) {
      /* Best effort. The signup itself is already saved; this was the extra. */
    }

    const done = this.interest.querySelector('[data-az-wl-interest-done]');
    if (done) done.hidden = false;

    track('waitlist_interest', { ...this.context, interest: choice });
  }
}

if (!customElements.get('az-waitlist')) {
  customElements.define('az-waitlist', AzWaitlist);
}

/**
 * The perfume-page call to action.
 *
 * The opening and closing is the browser's — this only puts the cursor in the
 * field and records the intent, so the control still works when this file
 * does not load.
 */
function initDisclosures() {
  for (const disclosure of document.querySelectorAll('[data-az-wl-disclosure]')) {
    if (disclosure.dataset.azWlBound) continue;
    disclosure.dataset.azWlBound = 'true';

    disclosure.addEventListener('toggle', () => {
      if (!disclosure.open) return;

      const waitlist = disclosure.closest('az-waitlist');
      disclosure.querySelector('.az-wl__input')?.focus({ preventScroll: true });
      track('product_waitlist_click', {
        source: waitlist?.dataset.source || 'product_page',
        product_handle: waitlist?.dataset.productHandle || null,
        product_title: waitlist?.dataset.productTitle || null,
      });
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDisclosures, { once: true });
} else {
  initDisclosures();
}

/**
 * The countdown, when one has been switched on.
 *
 * Counts to a timestamp configured in theme settings, so every visitor sees
 * the same number and a reload cannot restart it. Once the date has passed it
 * removes itself and leaves the date behind rather than counting downward past
 * zero. Off unless a real date has been entered.
 */
class AzCountdown extends HTMLElement {
  connectedCallback() {
    this.at = Date.parse(this.dataset.at || '');
    if (Number.isNaN(this.at)) return;

    // The server-rendered date stays as the fallback if the clock is spent.
    this.fallback = this.innerHTML;
    this.tick();
    this.timer = window.setInterval(() => this.tick(), 60_000);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  tick() {
    const remaining = this.at - Date.now();

    if (remaining <= 0) {
      window.clearInterval(this.timer);
      this.innerHTML = this.fallback;
      return;
    }

    const days = Math.floor(remaining / 86_400_000);
    const hours = Math.floor((remaining % 86_400_000) / 3_600_000);
    const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

    this.textContent =
      days > 0 ? `${plural(days, 'day')}, ${plural(hours, 'hour')}` : plural(hours, 'hour');
  }
}

if (!customElements.get('az-countdown')) {
  customElements.define('az-countdown', AzCountdown);
}
